import React from 'react';
import { AlertCircle, TrendingDown, TrendingUp, Minus, Info, Tag } from 'lucide-react';
import { cn } from '../../utils/utils';

// ── Shared pricing-action tag config ──────────────────────────────────────────
// Single source of truth for the colour coding of `pricing_action_tag` /
// `pricing_action_tags`. Consumed by the PCP Dashboard "Pricing Actions" tab
// (PricingActions.jsx) and the Saved Searches list (savedSearches.jsx).
export const PRICING_ACTION_TAG_CONFIG = {
  hold: {
    label: 'Hold',
    description: 'Current pricing is optimal',
    Icon: Minus,
    accentColor: '#9ca3af',
    cardBg: 'bg-gray-50',
    headerColor: 'text-gray-700',
    descColor: 'text-gray-500',
    iconColor: 'text-gray-500',
    badgeBg: 'bg-gray-200 text-gray-600',
  },
  pull: {
    label: 'Pull / Liquidate',
    description: 'Immediate action required - review listing details, videos, recut, recert, etc.',
    Icon: AlertCircle,
    accentColor: '#ef4444',
    cardBg: 'bg-red-50',
    headerColor: 'text-red-600',
    descColor: 'text-red-500',
    iconColor: 'text-red-500',
    badgeBg: 'bg-red-100 text-red-700',
  },
  aggressive_cut: {
    label: 'Aggressive Cut',
    description: 'Price reduction of 2-4% recommended',
    Icon: TrendingDown,
    accentColor: '#fb923c',
    cardBg: 'bg-orange-50',
    headerColor: 'text-orange-600',
    descColor: 'text-orange-500',
    iconColor: 'text-orange-500',
    badgeBg: 'bg-orange-100 text-orange-700',
  },
  medium_cut: {
    label: 'Medium Cut',
    description: 'Price reduction of 1-2% recommended',
    Icon: TrendingDown,
    accentColor: '#f59e0b',
    cardBg: 'bg-amber-50',
    headerColor: 'text-amber-700',
    descColor: 'text-amber-600',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-100 text-amber-700',
  },
  small_cut: {
    label: 'Small Cut',
    description: 'Price reduction of 0.5-1.0% recommended',
    Icon: Minus,
    accentColor: '#facc15',
    cardBg: 'bg-yellow-50',
    headerColor: 'text-yellow-700',
    descColor: 'text-yellow-600',
    iconColor: 'text-yellow-500',
    badgeBg: 'bg-yellow-100 text-yellow-700',
  },
  increase: {
    label: 'Increase',
    description: 'Price increase recommended',
    Icon: TrendingUp,
    accentColor: '#60a5fa',
    cardBg: 'bg-blue-50',
    headerColor: 'text-blue-600',
    descColor: 'text-blue-500',
    iconColor: 'text-blue-500',
    badgeBg: 'bg-blue-100 text-blue-700',
  },
  review_criteria: {
    label: 'Review Criteria',
    description: 'Review the filter criteria / competitions as there are very few to compare against',
    Icon: Info,
    accentColor: '#10b981',
    cardBg: 'bg-emerald-50',
    headerColor: 'text-emerald-700',
    descColor: 'text-emerald-600',
    iconColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-100 text-emerald-700',
  },
};

export const DEFAULT_PRICING_ACTION_TAG_CONFIG = {
  label: 'Pricing Action',
  description: '',
  Icon: TrendingUp,
  accentColor: '#d1d5db',
  cardBg: 'bg-gray-50',
  headerColor: 'text-gray-700',
  descColor: 'text-gray-500',
  iconColor: 'text-gray-500',
  badgeBg: 'bg-gray-100 text-gray-600',
};

// "aggressive_cut" -> "Aggressive Cut" — fallback label for tags the backend
// adds before the frontend knows about them.
export function humanizePricingActionTag(tag) {
  return String(tag)
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getPricingActionTagConfig(tag) {
  if (!tag) return null;
  return (
    PRICING_ACTION_TAG_CONFIG[tag] ?? {
      ...DEFAULT_PRICING_ACTION_TAG_CONFIG,
      label: humanizePricingActionTag(tag),
    }
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function PricingActionTagBadge({ tag, className }) {
  const cfg = getPricingActionTagConfig(tag);
  if (!cfg) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none whitespace-nowrap',
        cfg.badgeBg,
        className,
      )}
      title={cfg.description || cfg.label}
    >
      <Tag className="h-3 w-3 shrink-0" />
      {cfg.label}
    </span>
  );
}

export default PricingActionTagBadge;
